import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import './PriceBook.css';
import { PlusIcon } from '@heroicons/react/24/outline';

const MultiTierForm = ({ 
  item, 
  onSave, 
  onCancel 
}) => {
  const [tiers, setTiers] = useState([{
    id: null,
    tier_name: '',
    min_quantity: '',
    max_quantity: '',
    base_price: '',
    per_unit_price: '',
    setup_fee: '',
    tier_price: '',
    sort_order: 1,
    is_active: true,
    isNew: true
  }]);
  
  const [frequencies, setFrequencies] = useState([]);
  const [billingFrequencyId, setBillingFrequencyId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchFrequencies();
    fetchExistingTiers();
  }, [item]);

  const fetchFrequencies = async () => {
    try {
      const response = await api.get('/price-book/frequencies');
      setFrequencies(response.data);
    } catch (error) {
      console.error('Error fetching frequencies:', error);
    }
  };

  const fetchExistingTiers = async () => {
    try {
      const response = await api.get(`/price-book/items/${item.id}/tiers`);
      const existingTiers = response.data;
      if (existingTiers.length > 0) {
        // Set the billing frequency from the first tier (they should all be the same)
        setBillingFrequencyId(existingTiers[0].billing_frequency_id || '');
        
        setTiers(existingTiers.map((tier, index) => ({
          ...tier,
          base_price: tier.base_price || '',
          per_unit_price: tier.per_unit_price || '',
          setup_fee: tier.setup_fee || '',
          tier_price: tier.tier_price || '',
          min_quantity: tier.min_quantity || '',
          max_quantity: tier.max_quantity || '',
          sort_order: tier.sort_order || index + 1,
          isNew: false
        })));
      }
    } catch (error) {
      console.error('Error fetching existing tiers:', error);
    }
  };

  const addTier = () => {
    const newTier = {
      id: null,
      tier_name: '',
      min_quantity: '',
      max_quantity: '',
      base_price: '',
      per_unit_price: '',
      setup_fee: '',
      tier_price: '',
      sort_order: tiers.length + 1,
      is_active: true,
      isNew: true
    };
    setTiers([...tiers, newTier]);
  };

  const removeTier = (index) => {
    if (tiers.length > 1) {
      const newTiers = tiers.filter((_, i) => i !== index);
      // Update sort orders
      const updatedTiers = newTiers.map((tier, i) => ({
        ...tier,
        sort_order: i + 1
      }));
      setTiers(updatedTiers);
    }
  };

  const updateTier = (index, field, value) => {
    const newTiers = [...tiers];
    newTiers[index] = {
      ...newTiers[index],
      [field]: value
    };
    setTiers(newTiers);
  };

  const validateTiers = () => {
    // First check if billing frequency is selected
    if (!billingFrequencyId) {
      setError('Billing frequency is required');
      return false;
    }

    for (let i = 0; i < tiers.length; i++) {
      const tier = tiers[i];
      
      if (!tier.tier_name.trim()) {
        setError(`Tier ${i + 1}: Tier name is required`);
        return false;
      }

      const hasBasePrice = tier.base_price && parseFloat(tier.base_price) >= 0;
      const hasTierPrice = tier.tier_price && parseFloat(tier.tier_price) >= 0;
      
      if (!hasBasePrice && !hasTierPrice) {
        setError(`Tier ${i + 1}: Either base price or tier price is required`);
        return false;
      }

      // Validate quantity ranges
      const minQty = parseInt(tier.min_quantity);
      const maxQty = tier.max_quantity ? parseInt(tier.max_quantity) : null;
      
      if (isNaN(minQty) || minQty < 1) {
        setError(`Tier ${i + 1}: Minimum quantity must be at least 1`);
        return false;
      }
      
      if (maxQty && maxQty < minQty) {
        setError(`Tier ${i + 1}: Maximum quantity must be greater than or equal to minimum quantity`);
        return false;
      }

      // Check for overlapping ranges with other tiers
      for (let j = 0; j < tiers.length; j++) {
        if (i !== j) {
          const otherTier = tiers[j];
          const otherMinQty = parseInt(otherTier.min_quantity);
          const otherMaxQty = otherTier.max_quantity ? parseInt(otherTier.max_quantity) : null;
          
          if (!isNaN(otherMinQty)) {
            // Use the same logic as backend - check for problematic overlaps
            const newRangeMax = maxQty || Number.MAX_SAFE_INTEGER;
            const existingRangeMax = otherMaxQty || Number.MAX_SAFE_INTEGER;
            
            const hasProblematicOverlap = (
              (minQty < otherMinQty && newRangeMax > otherMinQty) ||  // New range starts before and extends into existing
              (minQty < existingRangeMax && newRangeMax > existingRangeMax) ||  // New range starts inside and extends beyond existing
              (minQty > otherMinQty && newRangeMax < existingRangeMax) ||  // New range is completely inside existing
              (minQty < otherMinQty && newRangeMax > existingRangeMax)     // New range completely contains existing
            );
            
            if (hasProblematicOverlap) {
              setError(`Tier ${i + 1}: Quantity range overlaps with Tier ${j + 1}`);
              return false;
            }
          }
        }
      }
    }
    
    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateTiers()) {
      return;
    }

    setLoading(true);
    try {
      const promises = tiers.map(async (tier) => {
        const tierData = {
          price_book_item_id: item.id,
          tier_name: tier.tier_name,
          min_quantity: parseInt(tier.min_quantity),
          max_quantity: tier.max_quantity ? parseInt(tier.max_quantity) : null,
          base_price: tier.base_price ? parseFloat(tier.base_price) : 0,
          per_unit_price: tier.per_unit_price ? parseFloat(tier.per_unit_price) : null,
          setup_fee: tier.setup_fee ? parseFloat(tier.setup_fee) : 0,
          tier_price: tier.tier_price ? parseFloat(tier.tier_price) : null,
          billing_frequency_id: parseInt(billingFrequencyId),
          sort_order: tier.sort_order,
          is_active: tier.is_active
        };

        if (tier.isNew) {
          const response = await api.post(`/price-book/items/${item.id}/tiers`, tierData);
          return response.data;
        } else {
          const response = await api.put(`/price-book/tiers/${tier.id}`, tierData);
          return response.data;
        }
      });

      await Promise.all(promises);
      onSave();
    } catch (error) {
      console.error('Error saving tiers:', error);
      setError('Failed to save tiers');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content large">
        <div className="modal-header">
          <h3>Manage Tiered Pricing</h3>
          <button className="close-button" onClick={onCancel}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="multi-tier-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-section">
            <h4>Item: {item?.item_name}</h4>
            <p className="item-description">{item?.description}</p>
          </div>

          <div className="form-section">
            <div className="form-group">
              <label htmlFor="billing_frequency_id">
                Billing Frequency (applies to all tiers) <span className="required">*</span>
              </label>
              <select
                id="billing_frequency_id"
                name="billing_frequency_id"
                value={billingFrequencyId}
                onChange={(e) => setBillingFrequencyId(e.target.value)}
                required
              >
                <option value="">Select frequency...</option>
                {frequencies.map(freq => (
                  <option key={freq.id} value={freq.id}>
                    {freq.display_label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="tiers-table-section">
            <div className="table-header">
              <h4>Price Tiers</h4>
              <button 
                type="button" 
                onClick={addTier}
                className="btn-secondary"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Tier
              </button>
            </div>

            <div className="tiers-table-container">
              <table className="tiers-table">
                <thead>
                  <tr>
                    <th>Tier Name *</th>
                    <th>Min Qty *</th>
                    <th>Max Qty</th>
                    <th>Base Price ($)</th>
                    <th>Per Unit ($)</th>
                    <th>Tier Price ($)</th>
                    <th>Setup Fee ($)</th>
                    {tiers.some(t => !t.isNew) && <th>Active</th>}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tiers.map((tier, index) => (
                    <tr key={index} className={!tier.is_active ? 'inactive-row' : ''}>
                      <td>
                        <input
                          type="text"
                          value={tier.tier_name}
                          onChange={(e) => updateTier(index, 'tier_name', e.target.value)}
                          placeholder="e.g., Basic, Premium"
                          className="table-input"
                          required
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={tier.min_quantity}
                          onChange={(e) => updateTier(index, 'min_quantity', e.target.value)}
                          min="1"
                          className="table-input number-input"
                          required
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={tier.max_quantity}
                          onChange={(e) => updateTier(index, 'max_quantity', e.target.value)}
                          placeholder="∞"
                          className="table-input number-input"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={tier.base_price}
                          onChange={(e) => updateTier(index, 'base_price', e.target.value)}
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          className="table-input number-input"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={tier.per_unit_price}
                          onChange={(e) => updateTier(index, 'per_unit_price', e.target.value)}
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          className="table-input number-input"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={tier.tier_price}
                          onChange={(e) => updateTier(index, 'tier_price', e.target.value)}
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          className="table-input number-input"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={tier.setup_fee}
                          onChange={(e) => updateTier(index, 'setup_fee', e.target.value)}
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          className="table-input number-input"
                        />
                      </td>
                      {tiers.some(t => !t.isNew) && (
                        <td>
                          {!tier.isNew && (
                            <input
                              type="checkbox"
                              checked={tier.is_active}
                              onChange={(e) => updateTier(index, 'is_active', e.target.checked)}
                              className="table-checkbox"
                            />
                          )}
                        </td>
                      )}
                      <td>
                        <button
                          type="button"
                          onClick={() => removeTier(index)}
                          disabled={tiers.length === 1}
                          className="remove-tier-btn-small"
                          title="Remove tier"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="table-footer">
              <p className="table-help">
                * Either Base Price OR Tier Price is required for each tier
              </p>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onCancel} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Saving...' : 'Save All Tiers'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MultiTierForm; 